import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'src/app/api';
import type { RootState } from 'src/store/reducer';
import { MfaExchangeResponse, MfaStatusResponse, StepUpMfaArgs, StepUpMfaResult } from './types';
import { userMfaManager } from 'src/userManager';

export enum MfaActionTypes {
  PerformStepUp = '@@common/mfa/PerformStepUp',
}

export const performStepUpMfa = createAsyncThunk<StepUpMfaResult, StepUpMfaArgs, { state: RootState; rejectValue: string }>(
  MfaActionTypes.PerformStepUp,
  async ({ service, actionType }, thunkApi) => {
    if (actionType === 'window') {
      const existing = thunkApi.getState().mfa.mfaUserId;
      const status = await fetchMfaStatus(service, existing).catch(() => null);
      if (status?.valid && status.userId) {
        return { userId: status.userId, service };
      }
    }

    const accessToken = await triggerMfaPopup();

    const { data } = await api.post<MfaExchangeResponse>(
      getMfaEndpoint(service, 'exchange'),
      { mfaToken: accessToken, actionType },
      { headers: { Authorization: `Bearer ${accessToken}` }, skipAuthOverride: true }
    );

    if (!data.success || !data.userId) {
      return thunkApi.rejectWithValue(data.error || 'Verification failed. The MFA token could not be validated.');
    }
    return { userId: data.userId, service };
  }
);

export const fetchMfaStatus = async (service: string, userId: string | null): Promise<MfaStatusResponse> => {
  if (!userId) {
    return { valid: false, actionType: null, expiresAt: null };
  }
  const { data } = await api.get<MfaStatusResponse>(getMfaEndpoint(service, 'status'), { params: { userId } });
  return data;
};

export const triggerMfaPopup = async (): Promise<string> => {
  const user = await userMfaManager.signinPopup({
    scope: 'openid profile allowedrisk:high',
  });
  return user.access_token;
};

export const getMfaEndpoint = (service: string, action: 'exchange' | 'status'): string => `/${service}/mfa/${action}`;
